import { createConnectTransport } from '@connectrpc/connect-web'
import { ConnectError, Code } from '@connectrpc/connect'
import { getStoredToken, getToken, removeToken } from '@/stores/token.store'
import { Sentry } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { shouldReportRpcError } from '@/lib/rpc-error-filter'
import {
  newRequestId,
  recordResponseCorrelation,
} from '@/lib/observability'

const publicLeadLinkMethods = new Set(['GetPublicInquiry', 'GetPublicQuote', 'MarkPublicQuoteViewed', 'AcceptPublicQuote', 'RejectPublicQuote'])

/**
 * Create Connect transport for communicating with the backend API from the portal
 *
 * The backend URL is configured via VITE_API_URL environment variable.
 * Portal tokens may be passed via URL parameters for recipient attribution.
 *
 * Public endpoints are available at: /api/v1/public/cortado.CortadoService/*
 * These endpoints are public; tokens are optional attribution context.
 *
 * Token handling:
 * 1. First checks localStorage for stored token when available
 * 2. Falls back to URL param if not in storage
 * 3. Route components store tokens on first visit, then remove them from the URL
 */
export const connectTransport = createConnectTransport({
  baseUrl: `${import.meta.env.VITE_API_URL}/api/v1/public`,
  interceptors: [
    (next) => async (request) => {
      let token: string | null = null

      // Extract ID from the request path if available
      // Path format: /cortado.v1.CortadoService/GetPublicInquiry or similar
      // We'll need to check localStorage based on the current page
      // Support both /lead/:id and /booking/:id routes
      const leadMatch = window.location.pathname.match(/\/lead\/([^/]+)/)
      const bookingMatch = window.location.pathname.match(/\/booking\/([^/]+)/)
      const leadId = leadMatch?.[1]
      const bookingId = bookingMatch?.[1]
      const resourceId = leadId || bookingId
      const allowPublicLeadLinkToken = !!leadId && publicLeadLinkMethods.has(request.method.name)

      // Try to get token from localStorage first (if we have a resource ID)
      if (resourceId && resourceId !== 'expired') {
        token = allowPublicLeadLinkToken ? getStoredToken(resourceId) : getToken(resourceId)
      }

      // Fallback to URL parameter (for initial visit before storage)
      // Note: do NOT remove the token from the URL here — the route's useEffect
      // handles URL cleanup after storing the token in localStorage. Removing it
      // here causes a race condition where the route effect can't find the token.
      if (!token) {
        const params = new URLSearchParams(window.location.search)
        token = params.get('token')
      }

      // Add authentication header if token is available
      if (token) {
        request.header.set('Authorization', `Bearer ${token}`)
      }
      const clientRequestId = newRequestId()
      request.header.set('X-Request-ID', clientRequestId)
      const rpcName = `${request.service.typeName}/${request.method.name}`

      // Note: Connect-Web automatically sets the correct Content-Type
      // Don't manually override it as it causes 415 Unsupported Media Type errors

      // Add Sentry breadcrumb for API call
      Sentry.addBreadcrumb({
        category: 'rpc.client',
        message: rpcName,
        level: 'info',
        data: { requestId: clientRequestId },
      })

      return Sentry.startSpan(
        {
          name: rpcName,
          op: 'rpc.client',
          attributes: {
            'rpc.system': 'connect',
            'rpc.service': request.service.typeName,
            'rpc.method': request.method.name,
            'http.request_id': clientRequestId,
          },
        },
        async (span) => {
          try {
            const response = await next(request)
            recordResponseCorrelation(response.header, span)
            span.setStatus({ code: 1 })
            return response
          } catch (error) {
        // The backend portal mount signals an invalid/expired portal token
        // with Code.Unauthenticated ("invalid or expired token" from the
        // portal token interceptor; some handlers do their own token parsing
        // and return the same code). It does NOT emit PermissionDenied today.
        //
        // Unauthenticated with a token attached means the customer HAD a
        // session that the backend now rejects (expired mid-session or
        // revoked): evict it and send them to the self-service expired page
        // (ResendLinkCard) instead of leaving them on a generic error state.
        // Without an attached token it's an anonymous hit on a token-required
        // endpoint, not an expiry — it must bubble up as a normal error.
        // The `resourceId !== 'expired'` guard prevents redirect loops from
        // requests issued on the expired pages themselves.
        const isUnauthenticated = error instanceof ConnectError && error.code === Code.Unauthenticated
        const isPermissionDenied = error instanceof ConnectError && error.code === Code.PermissionDenied
        if (resourceId && resourceId !== 'expired') {
          if (isUnauthenticated && token) {
            removeToken(resourceId)
            // The transport runs outside the router context, so navigate with
            // window.location. Pass the rejected token along — the expired pages
            // read `?token=` to look up org info and power the resend-link flow
            // (the backend parses expired tokens itself for those endpoints).
            const expiredPath = leadId ? '/lead/expired' : '/booking/expired'
            window.location.assign(`${expiredPath}?token=${encodeURIComponent(token)}`)
          } else if (isPermissionDenied) {
            // Genuine authorization failure: evict the stored token so it is
            // not replayed, but surface the error in-page — no redirect.
            removeToken(resourceId)
          }
        }

        const connectErr = ConnectError.from(error)
        const backendRequestId =
          connectErr.metadata?.get('X-Request-ID') || clientRequestId
        const backendTraceId = connectErr.metadata?.get('X-Trace-ID')
        span.setAttribute('server.request_id', backendRequestId)
        if (backendTraceId) {
          span.setAttribute('sentry.trace_id', backendTraceId)
        }
        span.setAttribute('rpc.connect_rpc.error_code', connectErr.code)
        span.setStatus({
          code: 2,
          message:
            connectErr.code === Code.Canceled ? 'cancelled' : 'internal_error',
        })
        if (shouldReportRpcError(error, request.method.name)) {
          reportError(error, {
            feature: 'api',
            apiService: request.service.typeName,
            apiMethod: request.method.name,
            backendRequestId,
            backendTraceId,
          })
        }
        throw error
          }
        }
      )
    },
  ],
})
